import axios from "axios"
import moment from "moment"
import { useEffect, useRef, useState } from "react"
import { NotificationManager } from "react-notifications"
import { useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import sortAsce from "../../assets/svgs/sort-ascending.svg"
import sortDesc from "../../assets/svgs/sort-descending.svg"
import swap from "../../assets/svgs/swap-vertical.svg"
import Loader from "../../components/Loader"
import ContactPagination from "../../components/Pagination/ContactPagination"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import { BASE_URL } from "../../utils/Element"
import { initialLogsFilterData } from "../../utils/initialData"
import LogsFilter from "./Component/LogsFilter"

const Logs = () => {
  const [config] = useAuth()
  const navigate = useNavigate()

  // ─── Search + Debounce states ────────────────────────────────
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef(null)

  const [LogsData, setLogsData] = useState([])
  const [itemPerPage, setItemPerPage] = useState(50)
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState("date")
  const [sortDirection, setSortDirection] = useState("desc")
  const [currentPage, setCurrentPage] = useState(1)
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  })
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialLogsFilterData
  })

  // ─── Debounce logic ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value
    const trimmed = value.startsWith(" ") ? value.trimStart() : value
    setSearch(trimmed)
  }

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // Only trigger if empty or ≥ 3 characters
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim())
      }
    }, 450)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`

    // Use debounced search value
    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`
    }

    const {
      interaction_type_user,
      activity_count,
      activity_start_count,
      activity_end_count,
      module, contact, interaction_type, description
    } = filters

    if (interaction_type_user) {
      params += `&interaction_type_user=${encodeURIComponent(interaction_type_user)}`
    }
    if (activity_count) {
      params += `&activity_count=${encodeURIComponent(activity_count)}`
    }
    if (activity_start_count) {
      params += `&activity_start_count=${encodeURIComponent(activity_start_count)}`
    }
    if (activity_end_count) {
      params += `&activity_end_count=${encodeURIComponent(activity_end_count)}`
    }
    if (module) {
      params += `&module=${encodeURIComponent(module)}`
    }

    if (contact?.id) params += `&contact=${encodeURIComponent(contact.id)}`

    if (interaction_type) {
      params += `&interaction_type=${encodeURIComponent(interaction_type)}`
    }
    if (description) {
      params += `&description=${encodeURIComponent(description)}`
    }
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`
    }
    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}`
    }

    return params
  }

  const fetchLogs = (filters = filterData) => {
    const queryParams = buildQueryParams(filters)
    setLoading(true)

    axios
      .get(`${BASE_URL}/activity-logs?${queryParams}`, config)
      .then((res) => {
        const apiData = res?.data?.data

        const logsArray = Array.isArray(apiData?.logs)
          ? apiData.logs
          : Array.isArray(apiData?.logs?.data)
            ? apiData.logs.data
            : []

        setLogsData(logsArray)

        setPaginationData({
          totalItems: apiData?.total ?? apiData?.logs?.total ?? 0,
          from: apiData?.from ?? apiData?.logs?.from ?? 0,
          to: apiData?.to ?? apiData?.logs?.to ?? 0,
          totalPages: apiData?.last_page ?? apiData?.logs?.last_page ?? 0,
        })
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  // ─── Main triggers ────────────────────────────────────────────
  useEffect(() => {
    fetchLogs(filterData)
  }, [sortBy, sortDirection, currentPage, itemPerPage, debouncedSearch])

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch])

  const onSuccess = (filters) => {
    fetchLogs(filters || filterData)
  }

  const handleNext = () => {
    if (currentPage < paginationData.totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Activity Pulse</p>

        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center">
            <LogsFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts">
              <input
                type="text"
                className="body-S"
                placeholder="Search"
                value={search}
                onChange={handleSearchChange}   // ← updated handler
                style={{ border: "1px solid #D8D8D8", marginTop: "0px" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="light-bg-L pb-3 table-container">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          {loading ? (
            <Loader />
          ) : LogsData?.length === 0 ? (
            <p className="body-N text-center mt-6 p-10">No Logs Found</p>
          ) : (
            <table className="contact-table two-rows-static light-bg-L">
              <thead>
                <tr className="uppercase body-N dark-M">
                  <th className="green-H">
                    <div className="table-header">
                      Date
                      <img
                        role="button"
                        src={sortBy !== "date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("date")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="sort icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      User name
                      <img
                        role="button"
                        src={sortBy !== "user_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("user_id")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="sort icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Module name
                      <img
                        role="button"
                        src={sortBy !== "module_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("module_type")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="sort icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Contact Name
                      <img
                        role="button"
                        src={sortBy !== "contact_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("contact_id")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="sort icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Description
                      <img
                        role="button"
                        src={sortBy !== "description" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("description")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="sort icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Interaction type
                      <img
                        role="button"
                        src={sortBy !== "interaction_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("interaction_type")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="sort icon"
                      />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {LogsData.map((el, idx) => (
                  <tr key={el.id || idx} className="dark-H body-N">
                    <td>
                      {el.date ? moment(el.date).format("MM/DD/YY") : ""}
                    </td>

                    <td className="green-H capitalize">
                      <Link
                        to={`/user/${el?.user_id}`}
                        onClick={(e) => {
                          e.preventDefault()
                          if (el?.user_id) {
                            navigate(`/user/${el?.user_id}`)
                          }
                        }}
                      >
                        {el.user_name}
                      </Link>
                    </td>

                    <td>{el.module}</td>

                    <td className="green-H capitalize">
                      <Link
                        to={`/contact/${el?.contact?.id}`}
                        onClick={(e) => {
                          e.preventDefault()
                          if (el?.contact?.id) {
                            navigate(`/contact/${el?.contact?.id}`)
                          }
                        }}
                      >
                        {el?.contact?.name ?? ""}
                      </Link>
                    </td>

                    <td className="max-w-[400px] whitespace-normal break-words leading-5">
                      <div dangerouslySetInnerHTML={{ __html: el.description }} />
                    </td>


                    <td>{el.interaction_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {LogsData.length !== 0 && (
          <ContactPagination
            from="Logs"
            onSuccess={onSuccess}
            paginationData={paginationData}
            handleNext={handleNext}
            handlePrev={handlePrev}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => {
              setItemPerPage(value)
              setCurrentPage(1)
            }}
          />
        )}
      </div>
    </BaseLayout>
  )
}

export default Logs
